import { lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { COMPANY_HOME_LEGACY_PATH, COMPANY_HOME_PATH } from "@/pages/dashboard/dashboardTab.const";
import { ProtectedRoute } from "./ProtectedRoute";
import { GuestRoute } from "./GuestRoute";
import { AdminProtectedRoute } from "./AdminProtectedRoute";
import { FacilitatorProtectedRoute } from "./FacilitatorProtectedRoute";
import { EcosystemHeadProtectedRoute } from "./EcosystemHeadProtectedRoute";
import { ExpoHeadProtectedRoute } from "./ExpoHeadProtectedRoute";
import { ExpertProtectedRoute } from "./ExpertProtectedRoute";
import { ManagerManagementRoute } from "./ManagerManagementRoute";
import { BootFallback } from "./RouteFallback";
import { FlexDemoProvider } from "@/flex-demo/FlexDemoProvider";
import {
  ExpertMembershipJoinRedirect,
  ExpertMembershipApprovalRedirect,
} from "@/pages/expert/ExpertMembershipRedirects";
import {
  COLLABORATION_BUY_PATH,
  COLLABORATION_WALLET_PATH,
  LEGACY_CREDITS_BUY_PATH,
  LEGACY_CREDITS_PATH,
} from "@/shared/constants/collaborationWallet.const";
import { MY_CLUSTER_PATH } from "@/pages/my-cluster/myCluster.const";
import {
  GuestDiscoverIndexRedirect,
  GuestHomeExploreTabRedirect,
  ClientExploreExposTabRedirect,
  LegacyGuestCorridorProfileRedirect,
  LegacyGuestExpertProfileRedirect,
} from "./GuestExploreRedirects";
import LandingLayout from "@/components/layout/LandingPageLayout";
import GuestHomePage from "@/pages/discover/GuestHomePage";

const AppLayout = lazy(() => import("@/components/layout/AppLayout"));
const AdminLayout = lazy(() => import("@/components/layout/AdminLayout"));
const FacilitatorLayout = lazy(() => import("@/components/layout/FacilitatorLayout"));
const EcosystemHeadLayout = lazy(() => import("@/components/layout/EcosystemHeadLayout"));
const ExpoHeadLayout = lazy(() => import("@/components/layout/ExpoHeadLayout"));
const ExpertLayout = lazy(() => import("@/components/layout/ExpertLayout"));

const AuthDiscoverPage = lazy(() => import("@/pages/discover/AuthDiscoverPage"));
const PartnerCompanySearchPage = lazy(() => import("@/pages/discover/PartnerCompanySearchPage"));
const SectorEcosystemPage = lazy(() => import("@/pages/sector-ecosystem/SectorEcosystemPage"));
const SectorOpportunitiesListPage = lazy(() =>
  import("@/pages/sector-ecosystem/SectorCompaniesListPage").then((m) => ({
    default: m.SectorOpportunitiesListPage,
  })),
);
const SectorCompaniesBrowsePage = lazy(() =>
  import("@/pages/sector-ecosystem/SectorCompaniesListPage").then((m) => ({
    default: m.SectorCompaniesBrowsePage,
  })),
);
const SectorCollaborationModelsListPage = lazy(() =>
  import("@/pages/sector-ecosystem/SectorCompaniesListPage").then((m) => ({
    default: m.SectorCollaborationModelsListPage,
  })),
);
const LoginPage = lazy(() => import("@/pages/auth/LoginPage"));
const LoginTwoFactorPage = lazy(() => import("@/two-factor/pages/LoginTwoFactorPage"));
const SignupPage = lazy(() => import("@/pages/signup/SignupPage"));
const CorridorSignupPage = lazy(() => import("@/pages/signup/CorridorSignupPage"));
const SignupDocumentsPage = lazy(() => import("@/pages/signup/SignupDocumentsPage"));
const RegistrationSuccessPage = lazy(() => import("@/pages/signup/RegistrationSuccessPage"));
const ForgotPassword = lazy(() => import("@/pages/forgot-password/ForgotPassword"));
const ResendEmail = lazy(() => import("@/pages/resend-email/ResendEmail"));
const ResetPassword = lazy(() => import("@/pages/reset-password/ResetPassword"));
const ActivateAccountPage = lazy(() => import("@/pages/activate-account/ActivateAccountPage"));
const ReviewAccountPage = lazy(() => import("@/pages/review-account/ReviewAccountPage"));
const EmailActionPage = lazy(() => import("@/pages/email-action/EmailActionPage"));

const Maintenance = lazy(() => import("@/pages/maintenance/Maintenance"));
const NotFoundPage = lazy(() => import("@/pages/NotFoundPage"));

const DiscoverCompanyProfileGate = lazy(() => import("@/routes/DiscoverCompanyProfileGate"));
const ExpoLoggedInCompanyProfile = lazy(() => import("@/routes/ExpoCompanyProfileGate"));
const GuestExpoCompanyProfile = lazy(() =>
  import("@/routes/ExpoCompanyProfileGate").then((m) => ({ default: m.GuestExpoCompanyProfile })),
);
const LegacyExpoCompanyProfileRedirect = lazy(() =>
  import("@/routes/ExpoCompanyProfileGate").then((m) => ({ default: m.LegacyExpoCompanyProfileRedirect })),
);
const ExpoDirectoryGate = lazy(() => import("@/routes/ExpoDirectoryGate"));
const GuestCorridorProfilePage = lazy(() => import("@/pages/discover/guest/GuestCorridorProfilePage"));
const GuestEcosystemDetailPage = lazy(() =>
  import("@/pages/ecosystems/EcosystemDetailPage").then((m) => ({
    default: m.GuestEcosystemDetailPage,
  })),
);
const MemberEcosystemDetailPage = lazy(() =>
  import("@/pages/ecosystems/EcosystemDetailPage").then((m) => ({
    default: m.MemberEcosystemDetailPage,
  })),
);
const GuestExpoDirectoryPage = lazy(() => import("@/pages/discover/guest/GuestExpoDirectoryPage"));
const GuestExpertProfilePage = lazy(() => import("@/pages/discover/guest/GuestExpertProfilePage"));
const AnonymousProfile = lazy(() => import("@/components/shared/AnonymousCompanyProfile/AnonymousProfile"));
const LoggedInCompanyWorkspaceRedirect = lazy(() => import("@/routes/LoggedInCompanyWorkspaceRedirect"));

function RedirectCompanyDashboardToHome() {
  const { search } = useLocation();
  return <Navigate to={`${COMPANY_HOME_PATH}${search}`} replace />;
}

const DashboardPage = lazy(() => import("@/pages/dashboard/DashboardPage"));
const UpdatesPage = lazy(() => import("@/pages/updates/UpdatesPage"));
const CreateProfilePage = lazy(() => import("@/pages/create-profile/CreateProfilePage"));
const MyProfilePage = lazy(() => import("@/pages/profile/MyProfilePage"));
const EditProfile = lazy(() => import("@/pages/edit-profile/EditProfile"));
const SettingsPage = lazy(() => import("@/pages/mysupport/SettingsPage"));
const UserAccountProfilePage = lazy(() => import("@/pages/account/UserAccountProfilePage"));
const EnableTwoFactorPage = lazy(() => import("@/two-factor/pages/EnableTwoFactorPage"));
const InterestsPage = lazy(() => import("@/pages/interests/InterestsPage"));
const MatchSuccessPage = lazy(() => import("@/pages/interests/components/ItsaMatchPage"));
const PaymentSuccessPage = lazy(() => import("@/pages/interests/components/PaymentSuccessPage"));
const CollaborationsPage = lazy(() => import("@/pages/collaborations/CollaborationsPage"));
const CollaborationWorkspacePage = lazy(() => import("@/pages/collaborations/CollaborationWorkspacePage"));
const CollaborationCompanyWorkspacePage = lazy(
  () => import("@/pages/collaborations/CollaborationCompanyWorkspacePage")
);
const FlexDemoPage = lazy(() => import("@/flex-demo/FlexDemoPage"));
const MatchedProfileView = lazy(() => import("@/pages/profile/MatchedView"));
const ShortlistPage = lazy(() => import("@/pages/shortlist/ShortlistPage"));
const MyClusterPage = lazy(() => import("@/pages/my-cluster/MyClusterPage"));
const ExposListPage = lazy(() => import("@/pages/expos/ExposListPage"));
const ExpoProfilePage = lazy(() => import("@/pages/expos/ExpoProfilePage"));
const MyExpoInquiriesPage = lazy(() => import("@/pages/expos/MyExpoInquiriesPage"));
const MyExposPage = lazy(() => import("@/pages/my-expos/MyExposPage"));
const ExhibitorInquiriesGuard = lazy(() => import("@/pages/my-expos/ExhibitorInquiriesGuard"));
const ExhibitorInquiriesPage = lazy(() => import("@/pages/my-expos/ExhibitorInquiriesPage"));
const ExhibitorExpoReachoutsPage = lazy(() => import("@/pages/my-expos/ExhibitorExpoReachoutsPage"));
const CollaborationsWalletPage = lazy(() => import("@/pages/collaboration-wallet/CollaborationsWalletPage"));
const BuyCollaborationsPage = lazy(() => import("@/pages/collaboration-wallet/BuyCollaborationsPage"));
const InvoicesPage = lazy(() => import("@/pages/invoices/InvoicesPage"));

const FacilitatorLoginPage = lazy(() => import("@/pages/facilitator/FacilitatorLoginPage"));
const FacilitatorDashboardPage = lazy(() => import("@/pages/facilitator/FacilitatorDashboardPage"));
const FacilitatorCompanyDetailPage = lazy(() => import("@/pages/facilitator/FacilitatorCompanyDetailPage"));
const FacilitatorCompanyAnonymousProfilePage = lazy(
  () => import("@/pages/facilitator/FacilitatorCompanyAnonymousProfilePage"),
);
const FacilitatorManagedCompanyProfilePage = lazy(
  () => import("@/pages/facilitator/FacilitatorManagedCompanyProfilePage"),
);
const FacilitatorAddCompanyPage = lazy(() => import("@/pages/facilitator/FacilitatorAddCompanyPage"));
const FacilitatorCreateCompanyPage = lazy(() => import("@/pages/facilitator/FacilitatorCreateCompanyPage"));
const FacilitatorPartnerRecommendationsPage = lazy(
  () => import("@/pages/facilitator/FacilitatorPartnerRecommendationsPage"),
);

const EcosystemHeadDashboardPage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadDashboardPage"));
const EcosystemHeadSubheadsPage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadSubheadsPage"));
const ExpoHeadDashboardPage = lazy(() => import("@/pages/expo-head/DashboardPage"));
const ExpoHeadMembersPage = lazy(() => import("@/pages/expo-head/MembersPage"));
const ExpoHeadReachOutsPage = lazy(() => import("@/pages/expo-head/ReachOutsPage"));
const ExpoHeadCompanyReachoutsPage = lazy(() => import("@/pages/expo-head/CompanyReachoutsPage"));
const ExpoHeadAnnouncementsPage = lazy(() => import("@/pages/expo-head/AnnouncementsPage"));
const ExpoHeadTimetablePage = lazy(() => import("@/pages/expo-head/TimetablePage"));
const ExpoHeadTimetableSlotPage = lazy(() => import("@/pages/expo-head/TimetableSlotPage"));
const ExpoHeadTeamPage = lazy(() => import("@/pages/expo-head/TeamPage"));
const ExpoHeadActivityLogPage = lazy(() => import("@/pages/expo-head/ActivityLogPage"));
const ExpoHeadCompanyAnonymousProfilePage = lazy(
  () => import("@/pages/expo-head/ExpoHeadCompanyAnonymousProfilePage"),
);
const EcosystemHeadStatsPage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadStatsPage"));
const EcosystemHeadRequestsPage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadRequestsPage"));
const EcosystemHeadQuestionnairePage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadQuestionnairePage"));
const EcosystemHeadVerificationPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadVerificationPage"),
);
const EcosystemHeadVerificationReviewPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadVerificationReviewPage"),
);
const EcosystemHeadAppointmentsPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadAppointmentsPage"),
);
const EcosystemHeadMessagesPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadMessagesPage"),
);
const EcosystemHeadEventsPage = lazy(() => import("@/pages/ecosystem-head/EcosystemHeadEventsPage"));
const EcosystemHeadAdvertisementsPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadAdvertisementsPage"),
);
const EcosystemHeadMeetingSchedulerPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadMeetingSchedulerPage"),
);
const EcosystemHeadPartnerRecommendationsPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadPartnerRecommendationsPage"),
);
const EcosystemHeadCompanyAnswersPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadCompanyAnswersPage"),
);
const EcosystemHeadManagedCompanyProfilePage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadManagedCompanyProfilePage"),
);
const EcosystemHeadCompareProfilesPage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadCompareProfilesPage"),
);
const CompanyEcosystemHeadWorkspacePage = lazy(
  () => import("@/pages/ecosystem-heads/CompanyEcosystemHeadWorkspacePage"),
);
const CompanyEcosystemHeadMoreInfoPage = lazy(
  () => import("@/pages/ecosystem-heads/CompanyEcosystemHeadMoreInfoPage"),
);
const CompanyEcosystemHeadAppointmentsPage = lazy(
  () => import("@/pages/ecosystem-heads/CompanyEcosystemHeadAppointmentsPage"),
);
const CompanyEcosystemHeadChatPage = lazy(
  () => import("@/pages/ecosystem-heads/CompanyEcosystemHeadChatPage"),
);
const EcosystemHeadCompanyAnonymousProfilePage = lazy(
  () => import("@/pages/ecosystem-head/EcosystemHeadCompanyAnonymousProfilePage"),
);
const EcosystemHeadShortlistPage = lazy(() => import("@/pages/ecosystem-heads/EcosystemHeadShortlistPage"));
const EcosystemHeadsIndustryPage = lazy(() => import("@/pages/ecosystem-heads/EcosystemHeadsIndustryPage"));
const ClientEcosystemHeadProfilePage = lazy(
  () => import("@/pages/ecosystem-heads/ClientEcosystemHeadProfilePage"),
);
const CorridorAnnouncementsPage = lazy(
  () => import("@/pages/ecosystem-heads/CorridorAnnouncementsPage"),
);
const ExpertDashboardPage = lazy(() => import("@/pages/expert/ExpertDashboardPage"));
const ExpertMembershipPage = lazy(() => import("@/pages/expert/ExpertMembershipPage"));
const ExpertQuestionnairePage = lazy(() => import("@/pages/expert/ExpertQuestionnairePage"));
const ExpertAppointmentsPage = lazy(() => import("@/pages/expert/ExpertAppointmentsPage"));
const ExpertMessagesPage = lazy(() => import("@/pages/expert/ExpertMessagesPage"));
const ExpertKnowledgeCenterPage = lazy(() => import("@/pages/expert/ExpertKnowledgeCenterPage"));
const ExpertEventsPage = lazy(() => import("@/pages/expert/ExpertEventsPage"));
const ExpertPartnerRecommendationsPage = lazy(
  () => import("@/pages/expert/ExpertPartnerRecommendationsPage"),
);
const ExpertCompanyAnswersPage = lazy(() => import("@/pages/expert/ExpertCompanyAnswersPage"));
const ExpertManagedCompanyProfilePage = lazy(
  () => import("@/pages/expert/ExpertManagedCompanyProfilePage"),
);
const CompanyExpertWorkspacePage = lazy(() => import("@/pages/experts/CompanyExpertWorkspacePage"));
const CompanyExpertMoreInfoPage = lazy(() => import("@/pages/experts/CompanyExpertMoreInfoPage"));
const CompanyExpertAppointmentsPage = lazy(
  () => import("@/pages/experts/CompanyExpertAppointmentsPage"),
);
const ExpertCompanyProfilePage = lazy(() => import("@/pages/expert/ExpertCompanyProfilePage"));
const ExpertEditCompanyProfilePage = lazy(() => import("@/pages/expert/ExpertEditCompanyProfilePage"));
const ExpertCompanyAnonymousProfilePage = lazy(
  () => import("@/pages/expert/ExpertCompanyAnonymousProfilePage"),
);
const ExpertsIndustryPage = lazy(() => import("@/pages/experts/ExpertsIndustryPage"));
const ClientExpertProfilePage = lazy(() => import("@/pages/experts/ClientExpertProfilePage"));
const ClientExpertCompanyProfilePage = lazy(
  () => import("@/pages/experts/ClientExpertCompanyProfilePage"),
);
const ExpertsAndCorridorsPage = lazy(
  () => import("@/pages/experts-and-corridors/ExpertsAndCorridorsPage"),
);

const AdminLoginPage = lazy(() => import("@/pages/admin/AdminLoginPage"));
const AdminUsersPage = lazy(() => import("@/pages/admin/users/AdminUsersPage"));
const AdminFacilitatorsPage = lazy(() => import("@/pages/admin/users/AdminFacilitatorsPage"));
const AdminFacilitatorDetailPage = lazy(() => import("@/pages/admin/users/AdminFacilitatorDetailPage"));
const AdminEcosystemHeadsPage = lazy(() => import("@/pages/admin/users/AdminEcosystemHeadsPage"));
const AdminEcosystemHeadCreatePage = lazy(
  () => import("@/pages/admin/users/AdminEcosystemHeadCreatePage"),
);
const AdminEcosystemHeadDetailPage = lazy(() => import("@/pages/admin/users/AdminEcosystemHeadDetailPage"));
const AdminEcosystemHeadEditPage = lazy(() => import("@/pages/admin/users/AdminEcosystemHeadEditPage"));
const AdminExposPage = lazy(() => import("@/pages/admin/expos/AdminExposPage"));
const AdminExpoDetailPage = lazy(() => import("@/pages/admin/expos/AdminExpoDetailPage"));
const AdminExpoEditPage = lazy(() => import("@/pages/admin/expos/AdminExpoEditPage"));
const AdminExpoTimetableSlotPage = lazy(
  () => import("@/pages/admin/expos/AdminExpoTimetableSlotPage"),
);
const AdminExpoVisitorsPage = lazy(() => import("@/pages/admin/users/AdminExpoVisitorsPage"));
const AdminCorridorSignupEditorPage = lazy(
  () => import("@/pages/admin/users/AdminCorridorSignupEditorPage"),
);
const AdminExpertsPage = lazy(() => import("@/pages/admin/users/AdminExpertsPage"));
const AdminExpertDetailPage = lazy(() => import("@/pages/admin/users/AdminExpertDetailPage"));
const AdminAddedExpertsPage = lazy(
  () => import("@/pages/admin/admin-added-experts/AdminAddedExpertsPage"),
);
const AdminManagersPage = lazy(() => import("@/pages/admin/users/AdminManagersPage"));
const SubAdminManagementPage = lazy(() => import("@/pages/admin/users/SubAdminManagementPage"));
const AdminOuterUsersPage = lazy(() => import("@/pages/admin/outer-users/AdminOuterUsersPage"));
const AdminCreditAdjustmentHistoryPage = lazy(
  () => import("@/pages/admin/users/AdminCreditAdjustmentHistoryPage")
);
const AdminBulkMailHistoryPage = lazy(() => import("@/pages/admin/bulk-mail/AdminBulkMailHistoryPage"));
const AdminBulkMailPage = lazy(() => import("@/pages/admin/bulk-mail/AdminBulkMailPage"));
const AdminStatusMailPage = lazy(() => import("@/pages/admin/status-mail/AdminStatusMailPage"));
const MiniCrmLayout = lazy(() => import("@/pages/admin/mini-crm/MiniCrmLayout"));
const MiniCrmCampaignsPage = lazy(() => import("@/pages/admin/mini-crm/MiniCrmCampaignsPage"));
const MiniCrmCampaignDetailPage = lazy(() => import("@/pages/admin/mini-crm/MiniCrmCampaignDetailPage"));
const MiniCrmTemplatePage = lazy(() => import("@/pages/admin/mini-crm/MiniCrmTemplatePage"));
const AdminCompanyOnboardingChartPage = lazy(
  () => import("@/pages/admin/company-lists/AdminCompanyOnboardingChartPage")
);
const AdminCountriesMapPage = lazy(() => import("@/pages/admin/company-lists/AdminCountriesMapPage"));
const AdminIndustriesMapPage = lazy(() => import("@/pages/admin/company-lists/AdminIndustriesMapPage"));
const AdminOverviewPage = lazy(() => import("@/pages/admin/dashboard/AdminOverviewPage"));
const AdminDashboardPage = lazy(() => import("@/pages/admin/company-lists/AdminDashboardPage"));
const AdminPendingVerificationPage = lazy(
  () => import("@/pages/admin/company-lists/AdminPendingVerificationPage")
);
const AdminIndirectVerificationPage = lazy(
  () => import("@/pages/admin/indirect-verification/AdminIndirectVerificationPage")
);
const AdminMeilisearchReindexPage = lazy(() => import("@/pages/admin/meilisearch/AdminMeilisearchReindexPage"));
const AdminSearchAnalyticsPage = lazy(
  () => import("@/pages/admin/search-analytics/AdminSearchAnalyticsPage"),
);
const AdminSarahHelpChatsPage = lazy(
  () => import("@/pages/admin/sarah-help/AdminSarahHelpChatsPage"),
);
const AdminSarahFaqPage = lazy(() => import("@/pages/admin/sarah-help/AdminSarahFaqPage"));
const AdminCollaborationTable = lazy(() => import("@/pages/admin/collaboration-lists/AdminCollaborationTable"));
const AdminInterestManagement = lazy(() => import("@/pages/admin/interest-management/InterestManagement"));
const AdminPartnerMatchPage = lazy(() => import("@/pages/admin/partner-match/AdminPartnerMatchPage"));
const AdminEcosystemHeadAssignPage = lazy(
  () => import("@/pages/admin/ecosystem-head-assign/AdminEcosystemHeadAssignPage"),
);
const AdminExpertAssignPage = lazy(() => import("@/pages/admin/expert-assign/AdminExpertAssignPage"));
const AdminFacilitatorSuggestionsPage = lazy(
  () => import("@/pages/admin/facilitator-suggestions/AdminFacilitatorSuggestionsPage"),
);
const AdminExpressInterestPreviewPage = lazy(
  () => import("@/pages/admin/express-interest/AdminExpressInterestPreviewPage")
);
const AdminExpressInterestClicksPage = lazy(
  () => import("@/pages/admin/express-interest/AdminExpressInterestClicksPage"),
);
const AdminCompanyIntentQuestionnaireListPage = lazy(
  () => import("@/pages/admin/company-lists/AdminCompanyIntentQuestionnaireListPage"),
);
const AdminDiscoverTable = lazy(() => import("@/pages/admin/discover-listings/AdminDiscoverTable"));
const AdminGuidancePage = lazy(() => import("@/pages/admin/guidance-table/AdminGuidancePage"));
const AdminPaymentSettingsPage = lazy(
  () => import("@/pages/admin/payment-settings/AdminPaymentSettingsPage"),
);
const AdminMailSettingsPage = lazy(
  () => import("@/pages/admin/mail-settings/AdminMailSettingsPage"),
);
const AdminGoogleCalendarPage = lazy(
  () => import("@/pages/admin/google-calendar/AdminGoogleCalendarPage"),
);
const AdminCompanyDetailView = lazy(() => import("@/pages/admin/company-lists/components/AdminMatchedView"));
const AdminCompanyFullEditPage = lazy(() => import("@/pages/admin/company-lists/AdminCompanyFullEditPage"));
const AdminOnboardingListPage = lazy(() => import("@/pages/admin/onboarding/AdminOnboardingListPage"));
const AdminOnboardingCreatePage = lazy(() => import("@/pages/admin/onboarding/AdminOnboardingCreatePage"));
const AdminOnboardingEditPage = lazy(() =>
  import("@/pages/admin/onboarding/AdminOnboardingCreatePage").then((m) => ({
    default: m.AdminOnboardingEditPage,
  }))
);
const AdminOnboardingWebsiteBatchesPage = lazy(
  () => import("@/pages/admin/onboarding/AdminOnboardingWebsiteBatchesPage"),
);
const AdminOnboardingWebsiteBatchReviewPage = lazy(
  () => import("@/pages/admin/onboarding/AdminOnboardingWebsiteBatchReviewPage"),
);
const AdminInvitedCompaniesListPage = lazy(
  () => import("@/pages/admin/invited-companies/AdminInvitedCompaniesListPage"),
);
const AdminInvitedCompanyBatchReviewPage = lazy(
  () => import("@/pages/admin/invited-companies/AdminInvitedCompanyBatchReviewPage"),
);
const AdminWebsiteProfilesListPage = lazy(
  () => import("@/pages/admin/website-profiles/AdminWebsiteProfilesListPage"),
);
const AdminWebsiteProfileBatchReviewPage = lazy(
  () => import("@/pages/admin/website-profiles/AdminWebsiteProfileBatchReviewPage"),
);
const AdminWebsiteProfileWorkerHealthPage = lazy(
  () => import("@/pages/admin/website-profiles/AdminWebsiteProfileWorkerHealthPage"),
);
const AdminHeadBookingsPage = lazy(() => import("@/pages/admin/AdminHeadBookingsPage"));
const AdminEcosystemNewsListPage = lazy(
  () => import("@/pages/admin/ecosystem-news/AdminEcosystemNewsListPage"),
);
const AdminEcosystemsListPage = lazy(
  () => import("@/pages/admin/ecosystems/AdminEcosystemsListPage"),
);
const AdminEcosystemCreatePage = lazy(
  () => import("@/pages/admin/ecosystems/AdminEcosystemFormPage"),
);
const AdminEcosystemEditPage = lazy(() =>
  import("@/pages/admin/ecosystems/AdminEcosystemFormPage").then((m) => ({
    default: m.AdminEcosystemEditPage,
  })),
);
const AdminEcosystemNewsCreatePage = lazy(
  () => import("@/pages/admin/ecosystem-news/AdminEcosystemNewsCreatePage"),
);
const AdminEcosystemNewsEditPage = lazy(() =>
  import("@/pages/admin/ecosystem-news/AdminEcosystemNewsCreatePage").then((m) => ({
    default: m.AdminEcosystemNewsEditPage,
  })),
);
const AdminTaxonomyAnonymizedHeroPage = lazy(
  () => import("@/pages/admin/taxonomy-anonymized-hero/AdminTaxonomyAnonymizedHeroPage"),
);
const AdminSectorPromoPage = lazy(() => import("@/pages/admin/sector-promo/AdminSectorPromoPage"));
const AdminPromoPopPage = lazy(() => import("@/pages/admin/promo-pop/AdminPromoPopPage"));
const AdminPageCtasPage = lazy(() => import("@/pages/admin/page-ctas/AdminPageCtasPage"));
const AdminPageCtaPreviewPage = lazy(() => import("@/pages/admin/page-ctas/AdminPageCtaPreviewPage"));
const AboutUs = lazy(() => import("@/pages/about/AboutUs"));
const PricingPage = lazy(() => import("@/pages/pricing/PricingPage"));
const AdminCollaborationDetailPage = lazy(
  () => import("@/pages/admin/collaboration-lists/components/WorkspaceView")
);
function AppRoutes() {
  return (
    <Suspense fallback={<BootFallback />}>
      <Routes>
        {/* Shared landing chrome — keep LandingLayout mounted across explore list ↔ profile */}
        <Route element={<LandingLayout />}>
          <Route element={<GuestRoute />}>
            <Route
              path="/"
              element={
                <GuestHomeExploreTabRedirect>
                  <GuestHomePage />
                </GuestHomeExploreTabRedirect>
              }
            />
            <Route path="/regional-sector-experts" element={<GuestHomePage />} />
            <Route path="/corridors-clusters" element={<GuestHomePage />} />
            <Route path="/global-events-exhibitions" element={<GuestHomePage />} />
            <Route path="/discover" element={<GuestDiscoverIndexRedirect />} />
          </Route>
          <Route
            path="/global-events-exhibitions/:slug/company/:id"
            element={<GuestExpoCompanyProfile />}
          />
          <Route
            path="/global-events-exhibitions/:slug"
            element={
              <ExpoDirectoryGate>
                <GuestExpoDirectoryPage />
              </ExpoDirectoryGate>
            }
          />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/pricing" element={<PricingPage />} />
          <Route
            path="/corridors-clusters/:industry/:headUserId"
            element={<GuestCorridorProfilePage />}
          />
          <Route path="/ecosystems/:ecosystemId" element={<GuestEcosystemDetailPage />} />
          <Route
            path="/regional-sector-experts/:id"
            element={<GuestExpertProfilePage />}
          />
          <Route
            path="/discover/corridor/:industry/:headUserId"
            element={<LegacyGuestCorridorProfileRedirect />}
          />
          <Route
            path="/discover/expert/company/:id"
            element={<LegacyGuestExpertProfileRedirect />}
          />
          <Route path="/discover/:id" element={<DiscoverCompanyProfileGate />}>
            <Route index element={<AnonymousProfile pageID="DISCOVER_PUBLIC" />} />
          </Route>
        </Route>
        <Route path="/registration-success" element={<RegistrationSuccessPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/login/2fa" element={<LoginTwoFactorPage />} />
        <Route path="/signup" element={<SignupPage />} />
        <Route path="/:corridorSlug/signup" element={<CorridorSignupPage />} />
        <Route path="/signup/company-details" element={<Navigate to="/login" replace />} />
        <Route path="/signup/documents" element={<SignupDocumentsPage />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/resend-email" element={<ResendEmail />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/activate-account" element={<ActivateAccountPage />} />
        <Route path="/review-account" element={<ReviewAccountPage />} />
        <Route path="/email-action" element={<EmailActionPage />} />
        <Route path="/facilitator/login" element={<FacilitatorLoginPage />} />

        {/* Protected routes with AppShell layout */}
        <Route
          element={
            <ProtectedRoute>
              <FlexDemoProvider>
                <AppLayout />
              </FlexDemoProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/flex-demo" element={<FlexDemoPage />} />
          <Route path={COMPANY_HOME_PATH} element={<DashboardPage />} />
          <Route path={COMPANY_HOME_LEGACY_PATH} element={<RedirectCompanyDashboardToHome />} />
          <Route path="/updates" element={<UpdatesPage />} />
          <Route path="/create-profile" element={<CreateProfilePage />} />
          <Route path="/profile" element={<MyProfilePage />} />
          <Route path={MY_CLUSTER_PATH} element={<MyClusterPage />} />
          <Route path="/expos" element={<ExposListPage />} />
          <Route path="/expos/company/:id" element={<LegacyExpoCompanyProfileRedirect />} />
          <Route path="/expos/:slug/company/:id" element={<ExpoLoggedInCompanyProfile />} />
          <Route path="/expos/:slug" element={<ExpoProfilePage />} />
          <Route path="/my-expos" element={<MyExposPage />} />
          <Route element={<ExhibitorInquiriesGuard />}>
            <Route path="/my-expos/inquiries" element={<ExhibitorInquiriesPage />} />
            <Route path="/my-expos/inquiries/:expoSlug" element={<ExhibitorExpoReachoutsPage />} />
          </Route>
          <Route
            path="/my-discovery"
            element={
              <ClientExploreExposTabRedirect>
                <AuthDiscoverPage />
              </ClientExploreExposTabRedirect>
            }
          />
          <Route path="/my-discovery/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/explore-ecosystems/:ecosystemId" element={<MemberEcosystemDetailPage />} />
          <Route path="/sector/:slug" element={<SectorEcosystemPage />} />
          <Route path="/sector/:slug/opportunities" element={<SectorOpportunitiesListPage />} />
          <Route path="/sector/:slug/companies" element={<SectorCompaniesBrowsePage />} />
          <Route path="/sector/:slug/collaboration-models" element={<SectorCollaborationModelsListPage />} />
          <Route path="/requests" element={<InterestsPage />} />
          <Route path="/collaborations" element={<CollaborationsPage />} />
          <Route path={COLLABORATION_WALLET_PATH} element={<CollaborationsWalletPage />} />
          <Route path={COLLABORATION_BUY_PATH} element={<BuyCollaborationsPage />} />
          <Route path="/shortlist" element={<ShortlistPage />} />
          <Route path="/inquiries" element={<MyExpoInquiriesPage />} />
          <Route path="/shortlist/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path={LEGACY_CREDITS_PATH} element={<Navigate to={COLLABORATION_WALLET_PATH} replace />} />
          <Route path={LEGACY_CREDITS_BUY_PATH} element={<Navigate to={COLLABORATION_BUY_PATH} replace />} />
          <Route path="/invoices" element={<InvoicesPage />} />
          <Route path="/invoices/wallet" element={<Navigate to={COLLABORATION_WALLET_PATH} replace />} />
          <Route path="/settings" element={<SettingsPage />} />
          <Route path="/account" element={<UserAccountProfilePage />} />
          <Route path="/ecosystem-heads" element={<Navigate to="/my-discovery?tab=corridors" replace />} />
          <Route path="/ecosystem-heads/shortlist" element={<EcosystemHeadShortlistPage />} />
          <Route path="/ecosystem-heads/my" element={<Navigate to="/my-discovery?tab=corridors" replace />} />
          <Route path="/ecosystem-heads/my/:linkId/companies" element={<Navigate to="/my-discovery?tab=corridors" replace />} />
          <Route path="/ecosystem-heads/my/:linkId/more-info" element={<CompanyEcosystemHeadMoreInfoPage />} />
          <Route path="/ecosystem-heads/my/:linkId/appointments" element={<CompanyEcosystemHeadAppointmentsPage />} />
          <Route path="/ecosystem-heads/my/:linkId/chat" element={<CompanyEcosystemHeadChatPage />} />
          <Route path="/ecosystem-heads/my/:linkId" element={<CompanyEcosystemHeadWorkspacePage />} />
          <Route path="/ecosystem-heads/sector/:sectorSlug" element={<EcosystemHeadsIndustryPage />} />
          <Route path="/ecosystem-heads/:industry" element={<EcosystemHeadsIndustryPage />} />
          <Route
            path="/ecosystem-heads/:industry/:headUserId/announcements"
            element={<CorridorAnnouncementsPage />}
          />
          <Route path="/ecosystem-heads/:industry/:headUserId" element={<ClientEcosystemHeadProfilePage />} />
          <Route path="/experts-and-corridors" element={<ExpertsAndCorridorsPage />} />
          <Route
            path="/experts"
            element={<Navigate to="/my-discovery?tab=experts" replace />}
          />
          <Route path="/experts/company/:id/profile" element={<ClientExpertCompanyProfilePage />} />
          <Route
            path="/experts/my"
            element={<Navigate to="/my-discovery?tab=experts" replace />}
          />
          <Route path="/experts/my/:linkId/more-info" element={<CompanyExpertMoreInfoPage />} />
          <Route path="/experts/my/:linkId/appointments" element={<CompanyExpertAppointmentsPage />} />
          <Route path="/experts/my/:linkId/chat" element={<Navigate to=".." replace />} />
          <Route path="/experts/my/:linkId" element={<CompanyExpertWorkspacePage />} />
          <Route path="/experts/sector/:sectorSlug" element={<ExpertsIndustryPage />} />
          <Route path="/experts/:industry" element={<ExpertsIndustryPage />} />
          <Route path="/experts/:industry/:expertUserId" element={<ClientExpertProfilePage />} />
          <Route path="/two-factor/setup" element={<EnableTwoFactorPage />} />
          <Route path="/profile/edit" element={<EditProfile />} />
          <Route path="/requests/:id" element={<LoggedInCompanyWorkspaceRedirect />} />
          <Route path="/requests/match-success/:id" element={<MatchSuccessPage />} />
          <Route path="/requests/payment-success/:id" element={<PaymentSuccessPage />} />

          <Route path="/collaborations/company/:companyId" element={<CollaborationCompanyWorkspacePage />} />
          <Route path="/collaborations/:id" element={<CollaborationWorkspacePage />} />
          <Route path="/collaborations/:id/profile" element={<MatchedProfileView />} />

          {/* Manager: facilitator-parity features inside company AppLayout */}
          <Route element={<ManagerManagementRoute />}>
            <Route path="/management" element={<FacilitatorDashboardPage />} />
            <Route path="/management/create-company" element={<FacilitatorCreateCompanyPage />} />
            <Route path="/management/add-company" element={<FacilitatorAddCompanyPage />} />
            <Route path="/management/partner-suggestions" element={<FacilitatorPartnerRecommendationsPage />} />
            <Route
              path="/management/companies/:id/company-profile"
              element={<FacilitatorManagedCompanyProfilePage />}
            />
            <Route path="/management/companies/:id/profile" element={<FacilitatorCompanyAnonymousProfilePage />} />
            <Route path="/management/companies/:id" element={<FacilitatorCompanyDetailPage />} />
          </Route>
        </Route>

        {/* ============ Admin routes ============ */}
        <Route path="/admin/login" element={<AdminLoginPage />} />

        <Route
          element={
            <AdminProtectedRoute>
              <AdminLayout />
            </AdminProtectedRoute>
          }
        >
          <Route path="/admin" element={<AdminOverviewPage />} />
          <Route path="/admin/users" element={<AdminUsersPage />} />
          <Route path="/admin/facilitators" element={<AdminFacilitatorsPage />} />
          <Route path="/admin/facilitators/:id" element={<AdminFacilitatorDetailPage />} />
          <Route path="/admin/ecosystem-heads">
            <Route index element={<AdminEcosystemHeadsPage />} />
            <Route path="new" element={<AdminEcosystemHeadCreatePage />} />
            <Route path=":id/signup-editor" element={<AdminCorridorSignupEditorPage />} />
            <Route path=":id/edit" element={<AdminEcosystemHeadEditPage />} />
            <Route path=":id" element={<AdminEcosystemHeadDetailPage />} />
          </Route>
          <Route path="/admin/ecosystems" element={<AdminEcosystemsListPage />} />
          <Route path="/admin/ecosystems/create" element={<AdminEcosystemCreatePage />} />
          <Route path="/admin/ecosystems/:id/edit" element={<AdminEcosystemEditPage />} />
          <Route path="/admin/expos">
            <Route index element={<AdminExposPage />} />
            <Route path=":id/edit" element={<AdminExpoEditPage />} />
            <Route path=":id/timetable/new" element={<AdminExpoTimetableSlotPage />} />
            <Route path=":id/timetable/:slotId/edit" element={<AdminExpoTimetableSlotPage />} />
            <Route path=":id" element={<AdminExpoDetailPage />} />
          </Route>
          <Route path="/admin/expo-visitors" element={<AdminExpoVisitorsPage />} />
          <Route path="/admin/experts" element={<AdminExpertsPage />} />
          <Route path="/admin/experts/:id" element={<AdminExpertDetailPage />} />
          <Route path="/admin/admin-added-experts" element={<AdminAddedExpertsPage />} />
          <Route path="/admin/managers" element={<AdminManagersPage />} />
          <Route path="/admin/sub-admins" element={<SubAdminManagementPage />} />
          <Route path="/admin/outer-users" element={<AdminOuterUsersPage />} />
          <Route path="/admin/credit-history" element={<AdminCreditAdjustmentHistoryPage />} />
          <Route path="/admin/payment-settings" element={<AdminPaymentSettingsPage />} />
          <Route path="/admin/mail-settings" element={<AdminMailSettingsPage />} />
          <Route path="/admin/google-calendar" element={<AdminGoogleCalendarPage />} />
          <Route path="/admin/bulk-mail/history" element={<AdminBulkMailHistoryPage />} />
          <Route path="/admin/bulk-mail" element={<AdminBulkMailPage />} />
          <Route path="/admin/status-mail" element={<AdminStatusMailPage />} />
          <Route path="/admin/mini-crm" element={<MiniCrmLayout />}>
            <Route index element={<MiniCrmCampaignsPage />} />
            <Route path="campaigns/:campaignId" element={<MiniCrmCampaignDetailPage />} />
            <Route path="templates/:templateId" element={<MiniCrmTemplatePage />} />
          </Route>
          <Route path="/admin/company-onboarding" element={<AdminCompanyOnboardingChartPage />} />
          <Route path="/admin/onboarding" element={<AdminOnboardingListPage />} />
          <Route path="/admin/onboarding/create" element={<AdminOnboardingCreatePage />} />
          <Route path="/admin/onboarding/website-batches" element={<AdminOnboardingWebsiteBatchesPage />} />
          <Route
            path="/admin/onboarding/website-batches/:batchId"
            element={<AdminOnboardingWebsiteBatchReviewPage />}
          />
          <Route path="/admin/onboarding/:id/edit" element={<AdminOnboardingEditPage />} />
          <Route path="/admin/invited-companies" element={<AdminInvitedCompaniesListPage />} />
          <Route path="/admin/invited-companies/:batchId" element={<AdminInvitedCompanyBatchReviewPage />} />
          <Route path="/admin/website-profiles" element={<AdminWebsiteProfilesListPage />} />
          <Route path="/admin/website-profiles/:batchId" element={<AdminWebsiteProfileBatchReviewPage />} />
          <Route path="/admin/website-profile-worker" element={<AdminWebsiteProfileWorkerHealthPage />} />
          <Route path="/admin/head-bookings" element={<AdminHeadBookingsPage />} />
          <Route path="/admin/ecosystem-news" element={<AdminEcosystemNewsListPage />} />
          <Route path="/admin/ecosystem-news/create" element={<AdminEcosystemNewsCreatePage />} />
          <Route path="/admin/ecosystem-news/:id/edit" element={<AdminEcosystemNewsEditPage />} />
          <Route path="/admin/taxonomy-anonymized-images" element={<AdminTaxonomyAnonymizedHeroPage />} />
          <Route path="/admin/sector-promos" element={<AdminSectorPromoPage />} />
          <Route path="/admin/promo-pops" element={<AdminPromoPopPage />} />
          <Route path="/admin/page-ctas/:id/preview" element={<AdminPageCtaPreviewPage />} />
          <Route path="/admin/page-ctas" element={<AdminPageCtasPage />} />
          <Route path="/admin/countries-map" element={<AdminCountriesMapPage />} />
          <Route path="/admin/industries-map" element={<AdminIndustriesMapPage />} />
          <Route path="/admin/companies" element={<AdminDashboardPage />} />
          <Route path="/admin/onboarding-qa" element={<AdminCompanyIntentQuestionnaireListPage />} />
          <Route path="/admin/pending-verification" element={<AdminPendingVerificationPage />} />
          <Route path="/admin/indirect-verification" element={<AdminIndirectVerificationPage />} />
          <Route path="/admin/meilisearch" element={<AdminMeilisearchReindexPage />} />
          <Route path="/admin/search-analytics" element={<AdminSearchAnalyticsPage />} />
          <Route path="/admin/sarah-help" element={<AdminSarahHelpChatsPage />} />
          <Route path="/admin/sarah-faq" element={<AdminSarahFaqPage />} />
          <Route path="/admin/collaborations" element={<AdminCollaborationTable />} />
          <Route path="/admin/interest" element={<AdminInterestManagement />} />
          <Route path="/admin/partner-match" element={<AdminPartnerMatchPage />} />
          <Route path="/admin/ecosystem-head-assign" element={<AdminEcosystemHeadAssignPage />} />
          <Route path="/admin/expert-assign" element={<AdminExpertAssignPage />} />
          <Route path="/admin/facilitator-suggestions" element={<AdminFacilitatorSuggestionsPage />} />
          <Route path="/admin/express-interest-preview" element={<AdminExpressInterestPreviewPage />} />
          <Route path="/admin/express-interest-clicks" element={<AdminExpressInterestClicksPage />} />
          <Route path="/admin/discover" element={<AdminDiscoverTable />} />
          <Route path="/admin/resource" element={<AdminGuidancePage />} />
          <Route path="/admin/companies/:id" element={<AdminCompanyDetailView />} />
          <Route path="/admin/companies/:id/edit" element={<AdminCompanyFullEditPage />} />
          <Route path="/admin/collaborations/:id" element={<AdminCollaborationDetailPage />} />
          <Route path="/admin/two-factor" element={<EnableTwoFactorPage />} />
        </Route>

        {/* ============ Ecosystem Head routes ============ */}
        <Route element={<EcosystemHeadProtectedRoute />}>
          <Route element={<EcosystemHeadLayout />}>
            <Route path="/ecosystem-head/my-discovery" element={<PartnerCompanySearchPage />} />
            <Route path="/ecosystem-head/shortlist" element={<ShortlistPage />} />
            <Route path="/ecosystem-head/partner-suggestions" element={<EcosystemHeadPartnerRecommendationsPage />} />
            <Route path="/ecosystem-head/dashboard" element={<EcosystemHeadDashboardPage />} />
            <Route path="/ecosystem-head/team" element={<EcosystemHeadSubheadsPage />} />
            <Route path="/ecosystem-head/ecosystem-members" element={<Navigate to="/ecosystem-head/team" replace />} />
            <Route path="/ecosystem-head/subheads" element={<Navigate to="/ecosystem-head/team" replace />} />
            <Route path="/ecosystem-head/updates" element={<UpdatesPage />} />
            <Route path="/ecosystem-head/stats" element={<EcosystemHeadStatsPage />} />
            <Route path="/ecosystem-head/requests" element={<EcosystemHeadRequestsPage />} />
            <Route path="/ecosystem-head/questionnaire" element={<EcosystemHeadQuestionnairePage />} />
            <Route path="/ecosystem-head/verification" element={<EcosystemHeadVerificationPage />} />
            <Route path="/ecosystem-head/verification/:linkId" element={<EcosystemHeadVerificationReviewPage />} />
            <Route path="/ecosystem-head/appointments" element={<EcosystemHeadAppointmentsPage />} />
            <Route path="/ecosystem-head/meeting-scheduler" element={<EcosystemHeadMeetingSchedulerPage />} />
            <Route path="/ecosystem-head/messages" element={<EcosystemHeadMessagesPage />} />
            <Route path="/ecosystem-head/events" element={<EcosystemHeadEventsPage />} />
            <Route path="/ecosystem-head/advertisements" element={<EcosystemHeadAdvertisementsPage />} />
            <Route path="/ecosystem-head/companies/:linkId/answers" element={<EcosystemHeadCompanyAnswersPage />} />
            <Route
              path="/ecosystem-head/companies/:id/company-profile"
              element={<EcosystemHeadManagedCompanyProfilePage />}
            />
            <Route
              path="/ecosystem-head/connection-reviews/:reviewId/compare"
              element={<EcosystemHeadCompareProfilesPage />}
            />
            <Route
              path="/ecosystem-head/companies/:id/profile"
              element={<EcosystemHeadCompanyAnonymousProfilePage />}
            />
            <Route path="/ecosystem-head/account" element={<UserAccountProfilePage />} />
            <Route path="/ecosystem-head/settings" element={<SettingsPage />} />
            <Route path="/ecosystem-head/two-factor/setup" element={<EnableTwoFactorPage />} />
          </Route>
        </Route>

        {/* ============ Expo Head routes ============ */}
        <Route element={<ExpoHeadProtectedRoute />}>
          <Route element={<ExpoHeadLayout />}>
            <Route path="/expo-head/dashboard" element={<ExpoHeadDashboardPage />} />
            <Route path="/expo-head/members" element={<ExpoHeadMembersPage />} />
            <Route path="/expo-head/reach-outs" element={<ExpoHeadReachOutsPage />} />
            <Route path="/expo-head/reach-outs/:companyId" element={<ExpoHeadCompanyReachoutsPage />} />
            <Route
              path="/expo-head/companies/:id/profile"
              element={<ExpoHeadCompanyAnonymousProfilePage />}
            />
            <Route path="/expo-head/announcements" element={<ExpoHeadAnnouncementsPage />} />
            <Route path="/expo-head/timetable" element={<ExpoHeadTimetablePage />} />
            <Route path="/expo-head/timetable/new" element={<ExpoHeadTimetableSlotPage />} />
            <Route path="/expo-head/timetable/:slotId/edit" element={<ExpoHeadTimetableSlotPage />} />
            <Route path="/expo-head/team" element={<ExpoHeadTeamPage />} />
            <Route path="/expo-head/activity" element={<ExpoHeadActivityLogPage />} />
            <Route path="/expo-head/updates" element={<UpdatesPage />} />
            <Route path="/expo-head/account" element={<UserAccountProfilePage />} />
            <Route path="/expo-head/settings" element={<SettingsPage />} />
            <Route path="/expo-head/two-factor/setup" element={<EnableTwoFactorPage />} />
          </Route>
        </Route>

        {/* ============ Expert routes ============ */}
        <Route element={<ExpertProtectedRoute />}>
          <Route element={<ExpertLayout />}>
            <Route path="/expert/my-discovery" element={<PartnerCompanySearchPage />} />
            <Route path="/expert/shortlist" element={<ShortlistPage />} />
            <Route path="/expert/partner-suggestions" element={<ExpertPartnerRecommendationsPage />} />
            <Route path="/expert/dashboard" element={<ExpertDashboardPage />} />
            <Route path="/expert/updates" element={<UpdatesPage />} />
            <Route path="/expert/membership" element={<ExpertMembershipPage />} />
            <Route path="/expert/requests" element={<ExpertMembershipJoinRedirect />} />
            <Route path="/expert/questionnaire" element={<ExpertQuestionnairePage />} />
            <Route path="/expert/verification" element={<ExpertMembershipApprovalRedirect />} />
            <Route path="/expert/appointments" element={<ExpertAppointmentsPage />} />
            <Route path="/expert/messages" element={<ExpertMessagesPage />} />
            <Route path="/expert/knowledge-center" element={<ExpertKnowledgeCenterPage />} />
            <Route path="/expert/events" element={<ExpertEventsPage />} />
            <Route path="/expert/companies/:linkId/answers" element={<ExpertCompanyAnswersPage />} />
            <Route
              path="/expert/companies/:id/company-profile"
              element={<ExpertManagedCompanyProfilePage />}
            />
            <Route path="/expert/companies/:id/profile" element={<ExpertCompanyAnonymousProfilePage />} />
            <Route path="/expert/company-profile" element={<ExpertCompanyProfilePage />} />
            <Route path="/expert/company-profile/edit" element={<ExpertEditCompanyProfilePage />} />
            <Route path="/expert/account" element={<UserAccountProfilePage />} />
            <Route path="/expert/settings" element={<SettingsPage />} />
            <Route path="/expert/two-factor/setup" element={<EnableTwoFactorPage />} />
          </Route>
        </Route>

        {/* ============ Facilitator routes ============ */}
        <Route element={<FacilitatorProtectedRoute />}>
          <Route element={<FacilitatorLayout />}>
            <Route path="/facilitator/dashboard" element={<FacilitatorDashboardPage />} />
            <Route path="/facilitator/updates" element={<UpdatesPage />} />
            <Route path="/facilitator/create-company" element={<FacilitatorCreateCompanyPage />} />
            <Route path="/facilitator/add-company" element={<FacilitatorAddCompanyPage />} />
            <Route path="/facilitator/managed" element={<FacilitatorDashboardPage />} />
            <Route path="/facilitator/my-discovery" element={<PartnerCompanySearchPage />} />
            <Route path="/facilitator/shortlist" element={<ShortlistPage />} />
            <Route path="/facilitator/partner-suggestions" element={<FacilitatorPartnerRecommendationsPage />} />
            <Route
              path="/facilitator/companies/:id/company-profile"
              element={<FacilitatorManagedCompanyProfilePage />}
            />
            <Route path="/facilitator/companies/:id/profile" element={<FacilitatorCompanyAnonymousProfilePage />} />
            <Route path="/facilitator/companies/:id" element={<FacilitatorCompanyDetailPage />} />
            <Route path="/facilitator/account" element={<UserAccountProfilePage />} />
            <Route path="/facilitator/settings" element={<SettingsPage />} />
            <Route path="/facilitator/two-factor/setup" element={<EnableTwoFactorPage />} />
          </Route>
        </Route>

        {/* 404 */}
        <Route path="*" element={<NotFoundPage />} />
        <Route path="/maintenance" element={<Maintenance />} />
      </Routes>
    </Suspense>
  );
}

export default AppRoutes;
